<script>
/**
 * 新版首页头部（自 vancheen-b2b 的 AppHeader.vue 移植为 Vue 2 版本）
 * ---------------------------------------------------------------
 * 布局：顶部信息条（左侧联系方式 + 右侧功能入口） + 主导航
 *
 * 顶部信息条右侧保留 2 个功能：
 *   1) 供应商登录 / 供应商后台
 *   2) 语言切换（English / 中文，复用老工程 i18n，切换后整页刷新）
 *
 * 主导航：首页 / 关于我们 / B2B大宗贸易(/topProduct) / B2C零售商城(/home) / 工厂参观。
 */
import NhSvgIcon from './NhSvgIcon.vue'
import NhLink from './NhLink.vue'
import asset from '../nh-asset'
import { LOCALE_LABELS, SUPPORTED_LOCALES, currentLocale, t as nhT } from '../nh-i18n'
import { getSite } from '../nh-site-data'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'NhHeader',
  components: { NhSvgIcon, NhLink },
  data() {
    return {
      scrolled: false,
      navOpen: false,
      openDrop: false,
      auth: {}
    }
  },
  computed: {
    ...mapState({
      supplierInfo: state => state.supplier.supplierInfo
    }),
    locale() {
      return currentLocale()
    },
    /** Vue 2 模板访问不到 import 常量，需在 computed 中显式暴露 */
    SUPPORTED_LOCALES() {
      return SUPPORTED_LOCALES
    },
    LOCALE_LABELS() {
      return LOCALE_LABELS
    },
    logoUrl() {
      return asset('logo.png')
    },
    CONTACT() {
      return getSite('CONTACT')
    },
    COMPANY() {
      return getSite('COMPANY')
    },
    NAV_ITEMS() {
      return getSite('NAV_ITEMS')
    },
    AUTO_SERIES() {
      return getSite('AUTO_SERIES')
    },
    BUILD_SERIES() {
      return getSite('BUILD_SERIES')
    }
  },
  watch: {
    navOpen(open) {
      document.body.style.overflow = open ? 'hidden' : ''
    }
  },
  mounted() {
    this.auth = JSON.parse(localStorage.getItem('auth')) || {}
    window.addEventListener('scroll', this.onScroll, { passive: true })
    this.onScroll()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
    document.body.style.overflow = ''
  },
  methods: {
    ...mapActions(['setLanguage']),
    nhT,
    /* ---------- 原版头部信息（联系方式/主导航） ---------- */
    seriesMain(s) {
      return currentLocale() === 'zh' ? s.zh : s.en
    },
    seriesSub(s) {
      return currentLocale() === 'zh' ? s.en : s.zh
    },
    onScroll() {
      this.scrolled = window.scrollY > 12
    },
    /** 当前导航项高亮（首页精确匹配，B2B/B2C 按各自路径，其余按路径前缀） */
    isActive(key) {
      const p = this.$route.path
      if (key === 'home') return p === '/' || p === '/old'
      if (key === 'b2b') return p.indexOf('/topProduct') === 0
      if (key === 'b2c') return p === '/home'
      return p.indexOf('/' + key) === 0
    },
    toggleNav() {
      this.navOpen = !this.navOpen
      if (!this.navOpen) this.openDrop = false
    },
    onNavLinkClick() {
      this.navOpen = false
      this.openDrop = false
    },
    toggleDrop() {
      if (window.innerWidth <= 900) this.openDrop = !this.openDrop
    },
    /** 移动端点击「Products」只展开子菜单、不跳转 */
    onMegaLinkClick(e) {
      if (window.innerWidth <= 900) {
        e.preventDefault()
        this.toggleDrop()
        return
      }
      this.onNavLinkClick()
    },
    /* ---------- 保留的页头功能（供应商登录 + 语言切换） ---------- */
    isAdminLogin() {
      return this.$store.getters.isAdminLogin
    },
    isLogin() {
      return this.$store.getters.isLogging
    },
    loginType() {
      return this.auth.type
    },
    getLang() {
      return this.$store.getters.getLanguage
    },
    /** 供应商登录 / 供应商后台 */
    gotoSupplierLogin() {
      this.$router.push({ path: '/login', query: { B2B: true } })
    },
    gotoUserPage() {
      if (this.supplierInfo && this.supplierInfo.B2bMode == 1) {
        this.$router.push('/public')
      } else if (this.supplierInfo && this.supplierInfo.B2b2cMode == 1) {
        this.$router.push('/supplier')
      } else {
        this.$router.push('/activate')
      }
    },
    /** 语言切换（老工程 i18n，切换后整页刷新；新首页文案随 Cookie 更新） */
    handleSetLanguage(lang) {
      this.setLanguage(lang)
      location.reload()
    }
  }
}
</script>

<template>
  <div>
    <!-- ===== 顶部信息条：联系方式 + 供应商登录 + 语言切换 ===== -->
    <div class="topbar">
      <div class="container">
        <div class="tb-left">
          <a :href="CONTACT.phoneHref">
            <NhSvgIcon name="phone" />{{ CONTACT.phone }}
          </a>
          <a :href="'mailto:' + CONTACT.email" class="mail">
            <NhSvgIcon name="mail" />{{ CONTACT.email }}
          </a>
        </div>
        <div class="tb-right">
          <!-- 供应商 -->
          <button
            v-if="isLogin() && loginType() == 'SU'"
            class="tb-func"
            @click="gotoUserPage"
          >
            {{ $t("B2B.header5") }}
          </button>
          <button v-else class="tb-func" @click="gotoSupplierLogin">
            {{ $t("B2B.header4") }}
          </button>
          <span class="tb-sep"></span>
          <!-- 语言切换 -->
          <button
            v-for="code in SUPPORTED_LOCALES"
            :key="code"
            class="tb-func tb-lang"
            :class="{ active: locale === code }"
            @click="handleSetLanguage(code)"
          >{{ LOCALE_LABELS[code] }}</button>
        </div>
      </div>
    </div>

    <!-- ===== 主导航 ===== -->
    <header class="site-header" :class="{ scrolled }">
      <div class="container header-inner">
        <NhLink to="/" class="logo">
          <span class="logo-mark"><img :src="logoUrl" alt="T&amp;W logo"></span>
          <span class="logo-text">
            <strong>{{ COMPANY.name }}</strong>
            <span>{{ COMPANY.subtitle }}</span>
          </span>
        </NhLink>

        <ul class="main-nav" :class="{ open: navOpen }">
          <!-- 单层 v-for（key 直接挂 li），mega/普通/站外三种形态用 li 内部的 template 分支区分 -->
          <li
            v-for="item in NAV_ITEMS"
            :key="item.key"
            :class="{ 'has-drop': item.mega, active: !item.external && isActive(item.key), open: item.mega && openDrop }"
            :data-nav="item.key"
          >
            <!-- 带 Mega 下拉的「Products」 -->
            <template v-if="item.mega">
              <NhLink :to="item.to" @click.native="onMegaLinkClick">
                {{ item.label }}<NhSvgIcon name="caret" />
              </NhLink>
              <div class="mega">
                <div class="mega-col">
                  <div class="mega-head auto">
                    <span class="pillar-ic"><NhSvgIcon name="car" /></span>
                    <div>
                      <h4>{{ nhT('header.megaAutoTitle') }}</h4>
                      <p>{{ nhT('header.megaAutoDesc') }}</p>
                    </div>
                  </div>
                  <div class="mega-list">
                    <NhLink
                      v-for="s in AUTO_SERIES"
                      :key="s.en"
                      to="/products?cat=auto"
                      @click.native="onNavLinkClick"
                    >
                      {{ seriesMain(s) }} <em>{{ seriesSub(s) }}</em>
                    </NhLink>
                    <NhLink class="mega-link-all" to="/products?cat=auto" @click.native="onNavLinkClick">
                      {{ nhT('header.viewAllAuto') }} <NhSvgIcon name="arrow" />
                    </NhLink>
                  </div>
                </div>
                <div class="mega-col">
                  <div class="mega-head building">
                    <span class="pillar-ic"><NhSvgIcon name="building" /></span>
                    <div>
                      <h4>{{ nhT('header.megaBuildingTitle') }}</h4>
                      <p>{{ nhT('header.megaBuildingDesc') }}</p>
                    </div>
                  </div>
                  <div class="mega-list">
                    <NhLink
                      v-for="s in BUILD_SERIES"
                      :key="s.en"
                      to="/products?cat=building"
                      @click.native="onNavLinkClick"
                    >
                      {{ seriesMain(s) }} <em>{{ seriesSub(s) }}</em>
                    </NhLink>
                    <NhLink class="mega-link-all" to="/products?cat=building" @click.native="onNavLinkClick">
                      {{ nhT('header.viewAllBuilding') }} <NhSvgIcon name="arrow" />
                    </NhLink>
                  </div>
                </div>
              </div>
            </template>

            <!-- 普通导航项 -->
            <NhLink v-else-if="!item.external" :to="item.to" @click.native="onNavLinkClick">{{ item.label }}</NhLink>

            <!-- 站外链接 -->
            <a v-else :href="item.href" target="_blank" rel="noopener">{{ item.label }}</a>
          </li>
        </ul>

        <div class="header-actions">
          <button class="nav-toggle" :class="{ open: navOpen }" aria-label="Menu" @click="toggleNav">
            <span></span><span></span><span></span>
          </button>
        </div>
      </div>
    </header>
  </div>
</template>

<style scoped>
/* ===== 顶部信息条右侧功能入口：白色字体（原 .lang button 风格的延伸） ===== */
.topbar .tb-func {
  appearance: none;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  font-family: inherit;
  font-size: 13px;
  font-weight: 300;
  color: #fff;
  cursor: pointer;
  white-space: nowrap;
  line-height: var(--topbar-h, 40px);
  transition: opacity 0.2s ease;
}
.topbar .tb-func:hover {
  opacity: 0.75;
}
/* 分隔竖线 */
.topbar .tb-sep {
  width: 1px;
  height: 14px;
  background: rgba(255, 255, 255, 0.4);
}
/* 语言切换：保留中英切换功能，当前语言加粗高亮 */
.topbar .tb-lang {
  padding: 0 6px;
  opacity: 0.7;
}
.topbar .tb-lang.active {
  font-weight: 700;
  opacity: 1;
}
</style>
